import React, { useEffect, useState } from "react";
import StudentSidebar from "../common/studentSidebar";
import ProfileCard from "../common/profileCard";
import apiInstance from "../../../utility/apiInstance";
import { useSelector } from "react-redux";
import { Note } from "../../../core/types/types";

const StudentNotes = () => {
    const user = useSelector((state: any) => state.auth.user);

    const [notes, setNotes] = useState<Note[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [addFormData, setAddFormData] = useState({ title: "", content: "" });
    const [addFormState, setAddFormState] = useState({ open: false, loading: false, error: "" });

    const [editFormData, setEditFormData] = useState({ title: "", content: "" });
    const [editFormState, setEditFormState] = useState({ open: false, selectedNote: "", loading: false, error: "" });

    const [deleteState, setDeleteState] = useState({ selectedNote: "", loading: false, error: "" });

    useEffect(() => {
        const fetchNotes = async () => {
            try {
                const response = await apiInstance.get("/api/notes", {
                    params: {
                        "where[student][equals]": user.id,
                        sort: "-createdAt",
                    },
                });
                setNotes(response.data.docs);
            } catch (error) {
                console.error("Error fetching notes:", error);
                setError("Failed to load notes. Please refresh the page.");
            } finally {
                setLoading(false);
            }
        };
        fetchNotes();
    }, [user?.id]);

    const openAddForm = () => {
        setAddFormData({ title: "", content: "" });
        setAddFormState({ open: true, loading: false, error: "" });
    };

    const addNote = async () => {
        if (!addFormData.title.trim()) return;
        setAddFormState({ open: true, loading: true, error: "" });
        try {
            const res = await apiInstance.post("/api/notes", {
                title: addFormData.title,
                content: addFormData.content,
                student: user.id,
            });
            setNotes((prev) => [res.data.doc, ...prev]);
            setAddFormData({ title: "", content: "" });
            setAddFormState({ open: false, loading: false, error: "" });
        } catch (error) {
            console.error(error);
            setAddFormState({ open: true, loading: false, error: "Failed to add the note. Please try again." });
            setTimeout(() => {
                setAddFormState((prev) => ({ ...prev, error: "" }));
            }, 10000);
        }
    };

    const openEditForm = (note: Note) => {
        setEditFormData({ title: note.title || "", content: note.content || "" });
        setEditFormState({ open: true, selectedNote: note.id || "", loading: false, error: "" });
    };

    const cancelEdit = () => {
        setEditFormState({ open: false, selectedNote: "", loading: false, error: "" });
    };

    const updateNote = async () => {
        if (!editFormData.title.trim()) return;
        setEditFormState((s) => ({ ...s, loading: true, error: "" }));
        try {
            const res = await apiInstance.patch(`/api/notes/${editFormState.selectedNote}`, editFormData);
            setNotes((prev) => prev.map((n) => (n.id === editFormState.selectedNote ? res.data.doc : n)));
            setEditFormState({ open: false, selectedNote: "", loading: false, error: "" });
        } catch (error) {
            console.error(error);
            setEditFormState((s) => ({ ...s, loading: false, error: "Failed to save. Please try again." }));
        }
    };

    const deleteNote = async (noteId: string | undefined) => {
        if (!noteId) return;
        setDeleteState({ selectedNote: noteId, loading: true, error: "" });
        try {
            await apiInstance.delete(`/api/notes/${noteId}`);
            setNotes((prev) => prev.filter((n) => n.id !== noteId));
            setDeleteState({ selectedNote: "", loading: false, error: "" });
        } catch (error) {
            console.error(error);
            setDeleteState({ selectedNote: noteId, loading: false, error: "Failed to delete. Please try again." });
        }
    };

    const isEditing = (noteId: string | undefined) => editFormState.open && editFormState.selectedNote === noteId;
    const isDeleting = (noteId: string | undefined) => deleteState.loading && deleteState.selectedNote === noteId;

    return (
        <div className="content">
            <div className="container">
                <ProfileCard />
                <div className="row">
                    <StudentSidebar />
                    <div className="col-lg-9">

                        {/* Page header */}
                        <div className="page-title d-flex align-items-center justify-content-between mb-4">
                            <h5 className="mb-0">My Notes</h5>
                            {!addFormState.open && (
                                <button className="btn btn-primary btn-sm" onClick={openAddForm}>
                                    <i className="fa-solid fa-plus me-1" />
                                    Add Note
                                </button>
                            )}
                        </div>

                        {error && (
                            <div className="alert alert-danger d-flex align-items-center gap-2 mb-3" role="alert">
                                <i className="fa-solid fa-circle-exclamation" />
                                {error}
                            </div>
                        )}

                        {/* Add form */}
                        {addFormState.open && (
                            <div className="card border shadow-sm mb-4">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <h6 className="mb-0">New Note</h6>
                                        <button
                                            className="btn btn-link p-0 text-muted"
                                            onClick={() => setAddFormState({ open: false, loading: false, error: "" })}
                                        >
                                            <i className="fa-solid fa-xmark" />
                                        </button>
                                    </div>
                                    <div className="mb-3">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Title *"
                                            value={addFormData.title}
                                            onChange={(e) => setAddFormData((f) => ({ ...f, title: e.target.value }))}
                                            autoFocus
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <textarea
                                            className="form-control"
                                            placeholder="Content (optional)"
                                            rows={4}
                                            value={addFormData.content}
                                            onChange={(e) => setAddFormData((f) => ({ ...f, content: e.target.value }))}
                                        />
                                    </div>
                                    {addFormState.error && (
                                        <div className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3" role="alert">
                                            <i className="fa-solid fa-circle-exclamation" />
                                            {addFormState.error}
                                        </div>
                                    )}
                                    <div className="d-flex gap-2">
                                        <button
                                            className="btn btn-primary btn-sm"
                                            onClick={addNote}
                                            disabled={addFormState.loading || !addFormData.title.trim()}
                                        >
                                            {addFormState.loading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                                    Saving...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fa-solid fa-check me-1" />
                                                    Save
                                                </>
                                            )}
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => setAddFormState({ open: false, loading: false, error: "" })}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Notes list */}
                        {loading ? (
                            <div className="d-flex align-items-center gap-2 text-muted py-4">
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                Loading notes...
                            </div>
                        ) : notes.length === 0 ? (
                            <div className="text-center py-5 text-muted">
                                <i className="isax isax-note-215 mb-2" style={{ fontSize: "2.5rem", display: "block" }} />
                                <p className="mb-0">No notes yet. Add your first note above.</p>
                            </div>
                        ) : (
                            <div className="row g-3">
                                {notes.map((note: Note) => (
                                    <div className="col-md-6" key={note.id}>
                                        <div className={`card h-100 border shadow-sm ${isEditing(note.id) ? "border-primary" : ""}`}>
                                            <div className="card-body">

                                                {/* Card header row */}
                                                <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                                                    {isEditing(note.id) ? (
                                                        <input
                                                            className="form-control form-control-sm fw-semibold"
                                                            value={editFormData.title}
                                                            onChange={(e) => setEditFormData((d) => ({ ...d, title: e.target.value }))}
                                                            autoFocus
                                                        />
                                                    ) : (
                                                        <h6 className="card-title fw-semibold mb-0">{note.title}</h6>
                                                    )}

                                                    <div className="d-flex gap-2 flex-shrink-0">
                                                        {isEditing(note.id) ? (
                                                            <>
                                                                <i
                                                                    className={`fa-solid fa-check fa-sm ${editFormState.loading || !editFormData.title.trim() ? "text-muted" : "text-success cursor-pointer"}`}
                                                                    title="Save"
                                                                    onClick={!editFormState.loading && editFormData.title.trim() ? updateNote : undefined}
                                                                />
                                                                {editFormState.loading
                                                                    ? <span className="spinner-border spinner-border-sm text-success" role="status" aria-hidden="true" style={{ width: "0.75rem", height: "0.75rem" }} />
                                                                    : <i
                                                                        className="fa-solid fa-xmark fa-sm text-secondary cursor-pointer"
                                                                        title="Cancel"
                                                                        onClick={cancelEdit}
                                                                    />
                                                                }
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i
                                                                    className={`fa-solid fa-pen fa-sm ${isDeleting(note.id) ? "text-muted" : "text-secondary cursor-pointer"}`}
                                                                    title="Edit"
                                                                    onClick={!isDeleting(note.id) ? () => openEditForm(note) : undefined}
                                                                />
                                                                {isDeleting(note.id)
                                                                    ? <span className="spinner-border spinner-border-sm text-danger" role="status" aria-hidden="true" style={{ width: "0.75rem", height: "0.75rem" }} />
                                                                    : <i
                                                                        className="fa-solid fa-trash fa-sm text-danger cursor-pointer"
                                                                        title="Delete"
                                                                        onClick={() => deleteNote(note.id)}
                                                                    />
                                                                }
                                                            </>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Content */}
                                                {isEditing(note.id) ? (
                                                    <textarea
                                                        className="form-control form-control-sm mt-2"
                                                        rows={3}
                                                        placeholder="Content (optional)"
                                                        value={editFormData.content}
                                                        onChange={(e) => setEditFormData((d) => ({ ...d, content: e.target.value }))}
                                                    />
                                                ) : (
                                                    note.content && (
                                                        <p className="card-text text-muted small mb-0" style={{ whiteSpace: "pre-wrap" }}>
                                                            {note.content}
                                                        </p>
                                                    )
                                                )}

                                                {/* Inline errors */}
                                                {editFormState.error && isEditing(note.id) && (
                                                    <div className="alert alert-danger d-flex align-items-center gap-2 py-1 px-2 mt-2 mb-0 small" role="alert">
                                                        <i className="fa-solid fa-circle-exclamation" />
                                                        {editFormState.error}
                                                    </div>
                                                )}
                                                {deleteState.error && deleteState.selectedNote === note.id && (
                                                    <div className="alert alert-danger d-flex align-items-center gap-2 py-1 px-2 mt-2 mb-0 small" role="alert">
                                                        <i className="fa-solid fa-circle-exclamation" />
                                                        {deleteState.error}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StudentNotes;
